import {ActionList, type ActionListItemProps} from '@primer/react'
import type {ComponentType, ReactNode} from 'react'

interface ActionBarItemSharedProps {
  children: ReactNode
  leadingVisual?: ComponentType
  trailingVisual?: ComponentType
  description?: ReactNode
  active?: boolean
  /** Stamped on the row element so focus reconciliation can key rows by path. */
  'data-artifact-path'?: string
}

export type ActionBarItemProps =
  | (ActionBarItemSharedProps & {
      href: string
      onClick?: never
    })
  | (ActionBarItemSharedProps & {
      href?: never
      onClick: NonNullable<ActionListItemProps['onSelect']>
    })

export function ActionBarItem({
  children,
  href,
  leadingVisual: LeadingVisual,
  trailingVisual: TrailingVisual,
  description,
  active,
  onClick,
  'data-artifact-path': dataArtifactPath,
}: ActionBarItemProps) {
  if (href !== undefined) {
    return (
      <ActionList.LinkItem href={href} active={active} data-artifact-path={dataArtifactPath}>
        {LeadingVisual && (
          <ActionList.LeadingVisual>
            <LeadingVisual />
          </ActionList.LeadingVisual>
        )}
        {children}
        {description !== undefined && description !== null && (
          <ActionList.Description>{description}</ActionList.Description>
        )}
        {TrailingVisual && (
          <ActionList.TrailingVisual>
            <TrailingVisual />
          </ActionList.TrailingVisual>
        )}
      </ActionList.LinkItem>
    )
  }

  return (
    <ActionList.Item
      active={active}
      aria-pressed={active === undefined ? undefined : active}
      onSelect={onClick}
      data-artifact-path={dataArtifactPath}
    >
      {LeadingVisual && (
        <ActionList.LeadingVisual>
          <LeadingVisual />
        </ActionList.LeadingVisual>
      )}
      {children}
      {description !== undefined && description !== null && (
        <ActionList.Description>{description}</ActionList.Description>
      )}
      {TrailingVisual && (
        <ActionList.TrailingVisual>
          <TrailingVisual />
        </ActionList.TrailingVisual>
      )}
    </ActionList.Item>
  )
}

ActionBarItem.displayName = 'ActionBarItem'
